/**
 * Browser reviewer-profile persistence for the attestation form.
 *
 * Profile is stored in localStorage under a single key so the attestation
 * form can prefill name, email, org, and a note template across sessions.
 * The profile never leaves the origin — it is never sent to the server.
 *
 * All three exported functions accept an optional `store` parameter
 * (narrower than `Storage`) so tests can inject a stub without monkeypatching
 * globalThis.localStorage. Production callers omit the parameter; the default
 * resolves to the real `localStorage` at call time (not at module-load time,
 * so it is safe in environments where localStorage initialises late).
 */
const PROFILE_KEY = "editor:reviewer-profile";
export function loadProfile(store = localStorage) {
    try {
        const raw = store.getItem(PROFILE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        // Validate minimum required fields.
        if (!parsed || typeof parsed.name !== "string" || typeof parsed.email !== "string") {
            return null;
        }
        return {
            name: parsed.name,
            email: parsed.email,
            org: typeof parsed.org === "string" ? parsed.org : undefined,
            noteTemplate: typeof parsed.noteTemplate === "string" ? parsed.noteTemplate : undefined,
        };
    }
    catch {
        // localStorage disabled (private mode) or malformed JSON; treat as no profile.
        return null;
    }
}
export function saveProfile(profile, store = localStorage) {
    try {
        store.setItem(PROFILE_KEY, JSON.stringify({
            name: profile.name,
            email: profile.email,
            org: profile.org ?? undefined,
            noteTemplate: profile.noteTemplate ?? undefined,
        }));
    }
    catch {
        // best-effort; quota / privacy denial is silently ignored
    }
}
export function clearProfile(store = localStorage) {
    try {
        store.removeItem(PROFILE_KEY);
    }
    catch {
        // best-effort
    }
}
